import React, { useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Search, Link as LinkIcon, Unlink, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function XeroLinkPanel({ account, reload }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [search, setSearch] = useState(account?.name || "");
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [working, setWorking] = useState(false);

  const linked = !!account?.xero_contact_id;

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!search || search.trim().length < 2) {
      toast.error("Enter at least 2 characters to search");
      return;
    }
    setSearching(true);
    setCandidates([]);
    try {
      const r = await api.get("/xero/contacts/search", { params: { q: search.trim() } });
      setCandidates(r.data);
      if (!r.data.length) toast.message("No Xero contacts found matching that name.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed (is Xero connected?)");
    } finally {
      setSearching(false);
    }
  };

  const autoMatch = async () => {
    setWorking(true);
    try {
      const r = await api.post(`/xero/match-account/${account.id}`);
      if (r.data.matched) {
        toast.success(`Linked: ${r.data.contact_name}`);
        reload();
      } else {
        toast.message("No close match found — try the manual search below.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-match failed");
    } finally { setWorking(false); }
  };

  const link = async (c) => {
    setWorking(true);
    try {
      await api.post(`/xero/link-account/${account.id}`, {
        xero_contact_id: c.contact_id,
        xero_contact_name: c.name,
      });
      toast.success(`Linked to ${c.name}`);
      reload();
      setCandidates([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed");
    } finally { setWorking(false); }
  };

  const unlink = async () => {
    if (!window.confirm("Unlink this account from Xero?")) return;
    setWorking(true);
    try {
      await api.post(`/xero/link-account/${account.id}`, { xero_contact_id: "" });
      toast.success("Unlinked");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlink failed");
    } finally { setWorking(false); }
  };

  return (
    <div className="space-y-5" data-testid="xero-link-panel">
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-blue-700" /> Xero Billing
          </CardTitle>
          {linked ? (
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="xero-linked-badge">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Linked
            </Badge>
          ) : (
            <Badge variant="outline" className="text-slate-500">Not linked</Badge>
          )}
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          {linked ? (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Row label="Xero contact name" value={account.xero_contact_name || "—"} />
                <Row label="Xero Contact ID" value={<code className="text-xs">{account.xero_contact_id}</code>} />
              </div>
              {isAdmin && (
                <div className="flex gap-2 pt-2 border-t border-slate-100">
                  <Button variant="outline" size="sm" onClick={unlink} disabled={working} data-testid="xero-unlink">
                    <Unlink className="h-3.5 w-3.5 mr-1" /> Unlink
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setCandidates([]); setSearch(account.name || ""); }}>
                    Re-link to a different contact
                  </Button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-500">
              Link this CRM account to a Xero contact to surface invoices and payment status.
              {!isAdmin && " (Admin only)"}
            </p>
          )}
        </CardContent>
      </Card>

      {isAdmin && (
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-sm font-heading flex items-center gap-2">
              <LinkIcon className="h-4 w-4 text-blue-700" />
              {linked ? "Re-link to a different Xero contact" : "Link to Xero"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 space-y-4">
            <div className="flex gap-2">
              <Button
                onClick={autoMatch}
                disabled={working}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="xero-auto-match"
              >
                <Sparkles className="h-4 w-4 mr-1" />
                Auto-match by name
              </Button>
              <span className="text-xs text-slate-400 self-center">— or search manually below —</span>
            </div>

            <form onSubmit={runSearch} className="flex gap-2" data-testid="xero-search-form">
              <Input
                placeholder="Search Xero contacts by name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="xero-search-input"
              />
              <Button type="submit" variant="outline" disabled={searching} data-testid="xero-search-btn">
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </form>

            {candidates.length > 0 && (
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-sm" data-testid="xero-candidates">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2 text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c) => (
                      <tr key={c.contact_id} className="border-b border-slate-100 hover:bg-blue-50/30">
                        <td className="px-3 py-2 font-medium">{c.name}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{c.email || "—"}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="text-[10px]">{c.status || "—"}</Badge>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="sm"
                            onClick={() => link(c)}
                            disabled={working}
                            data-testid={`xero-link-${c.contact_id}`}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <LinkIcon className="h-3.5 w-3.5 mr-1" /> Link
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-sm text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
